/**
 * v2 envelope: direct ChaCha20-Poly1305 AEAD with an HKDF-SHA256 subkey.
 * Byte-compatible with `backstop_crypto::envelope` on the Rust side.
 *
 * # Why v2
 *
 * v1 used `age` in scrypt-passphrase mode (work factor 18 = 262,144 rounds)
 * to bind AAD into the encryption. Scrypt is a KDF for stretching weak
 * passwords — feeding it a 256-bit uniformly-random symmetric key wasted
 * 100 ms – 3 s per operation for no security gain. See `envelope.rs` in
 * the Rust crate for the same rationale.
 *
 * # Wire format
 *
 *   [1 byte: version tag = 0x02][12 bytes: nonce][ciphertext || 16-byte tag]
 *
 * Overhead per message: 29 bytes (was ~250). Cost per op: microseconds.
 *
 * # Key derivation
 *
 *   subkey = HKDF-SHA256(salt = "backstop-envelope-v2", ikm = family_key)
 *
 * HKDF gives domain separation from any future subsystem that might also
 * use the family key.
 *
 * # AAD binding
 *
 * Passed straight to the AEAD. Wrong AAD → auth-tag mismatch → decrypt
 * throws.
 */

import { chacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';

const V2_TAG = 0x02;
const NONCE_LEN = 12;
const TAG_LEN = 16;
const HDR_LEN = 1 + NONCE_LEN;
const MIN_WIRE_LEN = HDR_LEN + TAG_LEN;
const HKDF_SALT = new TextEncoder().encode('backstop-envelope-v2');
const EMPTY = new Uint8Array(0);

function deriveKey(familyKey: Uint8Array): Uint8Array {
  if (familyKey.length !== 32) {
    throw new Error(
      `family key must be 32 bytes, got ${familyKey.length.toString()}`,
    );
  }
  return hkdf(sha256, familyKey, HKDF_SALT, EMPTY, 32);
}

/** Encrypt `plaintext` under `familyKey`, binding `aad` into the AEAD. */
export async function encrypt(
  familyKey: Uint8Array,
  plaintext: Uint8Array,
  aad: Uint8Array,
): Promise<Uint8Array> {
  const key = deriveKey(familyKey);
  const nonce = new Uint8Array(NONCE_LEN);
  crypto.getRandomValues(nonce);
  const ct = chacha20poly1305(key, nonce, aad).encrypt(plaintext);
  const out = new Uint8Array(HDR_LEN + ct.length);
  out[0] = V2_TAG;
  out.set(nonce, 1);
  out.set(ct, HDR_LEN);
  return out;
}

/** Decrypt `wire` under `familyKey`; throws on wrong AAD/key/version/tamper. */
export async function decrypt(
  familyKey: Uint8Array,
  wire: Uint8Array,
  aad: Uint8Array,
): Promise<Uint8Array> {
  if (wire.length < MIN_WIRE_LEN) {
    throw new Error(
      `envelope too short: ${wire.length.toString()} < ${MIN_WIRE_LEN.toString()}`,
    );
  }
  if (wire[0] !== V2_TAG) {
    throw new Error(
      `unsupported envelope version: 0x${(wire[0] ?? 0).toString(16).padStart(2, '0')}`,
    );
  }
  const key = deriveKey(familyKey);
  const nonce = wire.subarray(1, HDR_LEN);
  const ct = wire.subarray(HDR_LEN);
  return chacha20poly1305(key, nonce, aad).decrypt(ct);
}
